import React, { useState, useEffect, useRef } from 'react';
import styles from './EntitlementFieldLobbyResults.module.scss';
import {
  Configure,
  InstantSearch,
  useInfiniteHits,
  useInstantSearch,
  useStats,
} from 'react-instantsearch';
import { ZAKAUT, leumitSearchClient } from 'assets/constants';
import strings from "assets/strings";
import Text from "components/text/Text";
import { EntitlementFieldLobbyResult } from './EntitlementFieldLobbyResult';
import type { BaseHit, Hit } from 'instantsearch.js';
import { EntitlementResultType } from 'types';
import { VirtualRefinementList } from 'components/virtualRefinement/VirtualRefinmentList';
import SvgGenerator from 'components/svgGenerator/SvgGenerator';
import { Autocomplete } from 'components/inputs/autocomplete/Autocomplete';
import type { ProductItem } from 'components/inputs/autocomplete/Autocomplete';

function NoResults() {
  const noResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (noResultsRef.current) {
      noResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className={styles.noMessagesWrapper}
      role="status"
      aria-live="assertive"
      tabIndex={-1}
      ref={noResultsRef}
    >
      <SvgGenerator name="Magnifier" className="magnifierIcon" objectName="doctorSearchResults" />
      <Text text={strings.globalStrings.noResultsFound} />
    </div>
  );
}

function InfiniteHitsComponent() {
  const { hits } = useInfiniteHits();
  if (!hits.length) return null;

  return (
    <div>
      {hits.map((hit: Hit<BaseHit>) => (
        <EntitlementFieldLobbyResult key={hit.objectID} hit={hit as EntitlementResultType} />
      ))}
    </div>
  );
}

function SearchResultsView() {
  const { results, status } = useInstantSearch();
  const { hits } = useInfiniteHits();

  const extendedResults = results as typeof results & {
    __isError?: boolean;
    __errorStatus?: number;
  };

  if (extendedResults.__isError && extendedResults.__errorStatus === 403) {
    return (
      <div className={styles.noMessagesWrapper}>
        <Text text={strings.globalStrings.networkError} />
      </div>
    );
  }

  if ((status === 'idle' || status === 'error') && results.nbHits === 0) {
    return <NoResults />;
  }

  if (hits.length === 0 && status !== 'idle') {
    return null;
  }

  return <InfiniteHitsComponent />;
}

export function EntitlementFieldLobbyResults() {
  const [fieldDataId, setFieldDataId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    const div = document.querySelector('[data-id]');
    if (div) {
      const dataAttr = div.getAttribute('data-id');
      setFieldDataId(dataAttr);
    }
  }, []);

  const Stats: React.FC = () => {
    const { nbHits } = useStats();
    return nbHits ? (
      <div className={styles.resultsCount}>
        <Text
          className={styles.title}
          text={`${strings.entitlementFieldLobby.found} ${nbHits} ${strings.entitlementFieldLobby.entitlements}${strings.entitlementFieldLobby.fieldResultName}`}
        />
      </div>
    ) : null;
  };

  const triggerSubmit = () => {
    setSearchQuery("");
    setHasSubmitted(true);
  };

  const handleItemSelect = (item: ProductItem) => {
    setSearchQuery(item.serviceName);
    setHasSubmitted(true);
  };

  return (
    <div className="row ais-InstantSearch">
      <InstantSearch indexName={ZAKAUT} searchClient={leumitSearchClient}>
        <div className="col-12 ais-SearchResults">
          <div className={styles.searchDiv}>
            <div className={styles.searchBox}>
              <Autocomplete
                searchClient={leumitSearchClient}
                indexName={ZAKAUT}
                onItemSelect={handleItemSelect}
                fieldsIdList={fieldDataId || undefined}
                restrictedLetters={/[^A-Za-z0-9א-ת()\s\-]/g}
                placeholder={strings.entitlementFieldLobby.placeholder}
              />
              <Text
                text={strings.entitlementFieldLobby.showAllEntitlements}
                className={styles.serachTitle}
                onClick={triggerSubmit}
              />
            </div>
          </div>

          {hasSubmitted && (
            <>
              <Configure
                query={searchQuery || undefined}
                snippetEllipsisText="…"
                hitsPerPage={1000}
                filters={fieldDataId ? `fieldsIdList:"${fieldDataId}"` : ""}
              />
              <Stats />
              <SearchResultsView />
            </>
          )}

          <VirtualRefinementList attribute="fieldsNameList" />
        </div>
      </InstantSearch>
    </div>
  );
}
